import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, RefreshCw, Home } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import FinancialValuationService from '../../services/financialValuationService';
import assetService from '../../services/assetService';
import MasterDataService from '../../services/masterDataService';
import { useToast } from '../common/Toast';

const NewFinancialValuation = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [formData, setFormData] = useState({
    assetId: '',
    assetName: '',
    category: '',
    subcategory: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchasePrice: 0,
    currentValue: 0,
    depreciationMethod: 'Straight Line',
    depreciationRate: 20,
    usefulLife: 5,
    currency: 'USD',
    vendor: '',
    warrantyExpiry: '',
    location: '',
    department: '',
    assignedTo: '',
    status: 'Active',
    valuationMethod: 'Cost Model',
    notes: '',
  });

  // Load assets and categories on component mount
  useEffect(() => {
    loadAssets();
    loadCategories();
  }, []);

  const loadAssets = async () => {
    try {
      const response = await assetService.getAllAssets();
      if (response.success) {
        setAvailableAssets(response.data || []);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
    }
  };

  const loadCategories = async () => {
    try {
      // Extract unique categories from available assets
      const assetCategories = [...new Set(
        availableAssets.map(asset => asset.category || asset.type)
      )].filter(Boolean).sort();

      setAvailableCategories(assetCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssetSelect = (assetId) => {
    const selectedAsset = availableAssets.find(asset => String(asset._id || asset.id || '') === String(assetId));
    if (selectedAsset) {
      setFormData(prev => ({
        ...prev,
        assetId: assetId,
        assetName: selectedAsset.assetName || selectedAsset.name || '',
        category: selectedAsset.category || '',
        subcategory: selectedAsset.subcategory || '',
        purchaseDate: selectedAsset.purchaseDate ? new Date(selectedAsset.purchaseDate).toISOString().split('T')[0] : prev.purchaseDate,
        purchasePrice: selectedAsset.purchasePrice || 0,
        currentValue: selectedAsset.currentValue || 0,
        depreciationMethod: selectedAsset.depreciationMethod || 'Straight Line',
        depreciationRate: selectedAsset.depreciationRate || 20,
        usefulLife: selectedAsset.usefulLife || 5,
        currency: selectedAsset.currency || 'USD',
        vendor: selectedAsset.vendor || '',
        warrantyExpiry: selectedAsset.warrantyExpiry ? new Date(selectedAsset.warrantyExpiry).toISOString().split('T')[0] : '',
        location: selectedAsset.location || '',
        department: selectedAsset.department || '',
        assignedTo: selectedAsset.assignedTo || '',
        status: selectedAsset.status || 'Active',
        valuationMethod: selectedAsset.valuationMethod || 'Cost Model',
        notes: selectedAsset.notes || '',
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await FinancialValuationService.createFinancialValuation({
        assetId: formData.assetId,
        assetName: formData.assetName,
        assetIdString: formData.assetId,
        category: formData.category,
        subcategory: formData.subcategory,
        purchaseDate: formData.purchaseDate,
        purchasePrice: parseFloat(formData.purchasePrice),
        currentValue: parseFloat(formData.currentValue),
        depreciationMethod: formData.depreciationMethod,
        depreciationRate: parseFloat(formData.depreciationRate),
        usefulLife: parseInt(formData.usefulLife),
        currency: formData.currency,
        vendor: formData.vendor,
        warrantyExpiry: formData.warrantyExpiry || null,
        location: formData.location,
        department: formData.department,
        assignedTo: formData.assignedTo,
        status: formData.status,
        valuationMethod: formData.valuationMethod,
        notes: formData.notes,
        salvageValue: parseFloat(formData.salvageValue || 0),
        marketValue: parseFloat(formData.marketValue || formData.currentValue),
        insuranceValue: parseFloat(formData.insuranceValue || formData.purchasePrice),
        disposalValue: parseFloat(formData.disposalValue || formData.purchasePrice * 0.1),
      });

      if (response.success) {
        success('Financial valuation record created successfully');
        navigate('/assets/financial');
      } else {
        throw new Error(response.message || 'Failed to create record');
      }
    } catch (error) {
      console.error('Error creating financial valuation record:', error);
      showError('Failed to create financial valuation record');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/assets/financial');
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/overview', icon: <Home size={16} /> },
    { label: 'Assets', path: '/assets/management' },
    { label: 'Financial & Valuation', path: '/assets/financial' },
    { label: 'Add Financial Info', isLast: true }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <PageBreadcrumb items={breadcrumbItems} />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px',
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111827',
              margin: 0,
              fontFamily: 'var(--font-family)'
            }}>
              Add New Financial Valuation
            </h1>
            <p style={{
              color: '#6b7280',
              margin: '8px 0 0 0',
              fontSize: '14px'
            }}>
              Create a new financial valuation record for an asset
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              background: 'none',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '8px 16px',
              cursor: 'pointer',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <X size={16} />
            Cancel
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleFormSubmit} style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '32px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {/* Asset Selection */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Asset *
              </label>
              <select
                name="assetId"
                value={formData.assetId}
                onChange={(e) => {
                  handleAssetSelect(e.target.value);
                  handleFormChange(e);
                }}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select Asset</option>
                {availableAssets.map((asset) => {
                  const assetValue = String(asset._id || asset.id || '');
                  return (
                    <option key={assetValue} value={assetValue}>
                      {asset.assetCode || asset.assetId || asset.id} - {asset.name || 'Unnamed Asset'}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Category */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleFormChange}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select Category</option>
                {availableCategories.map((cat, idx) => (
                  <option key={`cat-${idx}`} value={cat.category || cat}>{cat.category || cat}</option>
                ))}
              </select>
            </div>

            {/* Purchase Price */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Purchase Price *
              </label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRight: 'none',
                  backgroundColor: '#f9fafb',
                  fontSize: '14px',
                  borderTopLeftRadius: '6px',
                  borderBottomLeftRadius: '6px'
                }}>
                  $
                </span>
                <input
                  type="number"
                  name="purchasePrice"
                  value={formData.purchasePrice}
                  onChange={handleFormChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0 6px 6px 0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)'
                  }}
                />
              </div>
            </div>

            {/* Current Value */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Current Value *
              </label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRight: 'none',
                  backgroundColor: '#f9fafb',
                  fontSize: '14px',
                  borderTopLeftRadius: '6px',
                  borderBottomLeftRadius: '6px'
                }}>
                  $
                </span>
                <input
                  type="number"
                  name="currentValue"
                  value={formData.currentValue}
                  onChange={handleFormChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0 6px 6px 0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)'
                  }}
                />
              </div>
            </div>

            {/* Depreciation Method */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Depreciation Method *
              </label>
              <select
                name="depreciationMethod"
                value={formData.depreciationMethod}
                onChange={handleFormChange}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white'
                }}
              >
                <option value="Straight Line">Straight Line</option>
                <option value="Declining Balance">Declining Balance</option>
                <option value="Sum of Years">Sum of Years</option>
                <option value="Units of Production">Units of Production</option>
              </select>
            </div>

            {/* Useful Life */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Useful Life (Years) *
              </label>
              <input
                type="number"
                name="usefulLife"
                value={formData.usefulLife}
                onChange={handleFormChange}
                required
                min="1"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)'
                }}
              />
            </div>

            {/* Purchase Date */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Purchase Date *
              </label>
              <input
                type="date"
                name="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleFormChange}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)'
                }}
              />
            </div>

            {/* Status */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleFormChange}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white'
                }}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Disposed">Disposed</option>
                <option value="Under Maintenance">Under Maintenance</option>
                <option value="Retired">Retired</option>
              </select>
            </div>
          </div>

          {/* Notes - Full Width */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleFormChange}
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Form Actions */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'flex-end',
            paddingTop: '24px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-outline"
              style={{
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                borderRadius: '6px',
                opacity: isLoading ? 0.6 : 1,
                fontSize: '14px'
              }}
            >
              {isLoading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Create Financial Valuation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewFinancialValuation;
